<template>
  <div id="app" :class="['app ' + $route.name]">
    <transition name="fade">
      <div v-if="pageLoaded" id="page" class="page">
        <transition name="fade">
          <Menu
            v-if="menuVisible"
            :is-auth="isAuth"
            :cart-contents="cartContents"
            :currency-code="currencyCode"
            @changeCurrency="changeCurrency"
            @setProductCategory="setProductCategory"
          />
        </transition>
        <div
          :class="['mob menu-icon', { active: menuVisible }]"
          @click="toggleMenu"
        >
          <span />
          <span />
          <span />
        </div>
        <div class="header-container">
          <Header
            :app-balance="appBalance"
            :is-auth="isAuth"
            :searched-product-text="searchedProductText"
            :cart-contents="cartContents"
            :currency-code="currencyCode"
            @searchSubmit="searchSubmit"
            @changeCurrency="changeCurrency"
            @setProductCategory="setProductCategory"
          />
        </div>
        <router-view
          :product-page-item="productPageItem"
          :is-auth="isAuth"
          :app-balance="appBalance"
          :products-filtered-by-search="productsFilteredBySearch"
          :add-to-cart-chosen-item="addToCartChosenItem"
          :currency="currency"
          :product-category="productCategory"
          :quality-options="qualityOptions"
          :text-page-header="textPageHeader"
          :text-page-content="textPageContent"
          :currency-code="currencyCode"
          @setAppBalance="setAppBalance"
          @addToCart="addToCart"
          @formatDate="formatDate"
          @goToProduct="goToProduct"
          @login="login"
          @forgotPass="forgotPass"
          @registration="registration"
          @setProductCategory="setProductCategory"
        />
        <div class="footer-container">
          <Footer
            v-if="
              isAuth ||
              (!isAuth && $route.name != 'Profile' && $route.name != 'Cart')
            "
            @setProductCategory="setProductCategory"
          />
        </div>
        <transition name="fade">
          <Login
            v-if="signInModalIsVisible"
            @closeSignIn="closeSignIn"
            @openSignUp="openSignUp"
            @login="login"
            @forgotPass="forgotPass"
          />
        </transition>
        <transition name="fade">
          <Registration
            v-if="signUpModalIsVisible"
            @closeSignUp="closeSignUp"
            @openSignIn="openSignIn"
            @registration="registration"
          />
        </transition>
        <transition name="fade">
          <Cart v-if="cartIsVisible" @setError="setError" />
        </transition>
        <transition name="fade">
          <ProductModal v-if="productModalIsVisible" />
        </transition>
        <transition name="fade">
          <TopUpModal
            v-if="topupModalIsVisible"
            @setError="setError"
            @openOrderModal="openOrderModal"
            @orderSubmit="orderSubmit"
          />
        </transition>
        <transition name="fade">
          <OrderModal
            v-if="orderModalIsVisible"
            :order-data="orderData"
            @setError="setError"
            @orderSubmit="orderSubmit"
          />
        </transition>
        <transition name="fade">
          <SellSkinModal
            v-if="sellSkinModalIsVisible"
            @setError="setError"
            @openOrderModal="openOrderModal"
          />
        </transition>
        <transition name="fade">
          <PayoutModal
            v-if="withdrawModalIsVisible"
            @setError="setError"
            @openOrderModal="openPayoutModal"
          />
        </transition>
        <div v-if="cookesModalIsVisible" class="modal-overlay">
          <div class="cookies-modal" v-if="!settingsManagerIsVisible">
            <div class="title">
              {{ $t("About Cookies") }}
            </div>
            <div class="desc">
              We employ cookies and similar technologies to enhance the
              functionality of our website, providing you with a seamless and
              personalized browsing experience. Some cookies are essential for
              basic functionality and are set automatically without requiring
              explicit consent.
              <br /><br />
              In addition to essential cookies, we may offer optional cookies
              and similar technologies that bring various benefits, further
              enhancing your interaction with our website. As these are
              non-essential, their usage depends on your preferences. For more
              information, please refer to our
              <a class="desc" @click="goToPage('cookie')">
                {{ $t("Cookie Policy") }} </a
              >.
            </div>

            <div class="buttons">
              <button class="button" @click="declineCookies()">
                <span>{{ $t("Decline") }}</span>
              </button>
              <button class="button" @click="acceptAllCookies()">
                <span>{{ $t("Accept") }}</span>
              </button>
              <button class="button" @click="settingsManagerIsVisible = true">
                <span>{{ $t("Manage cookies") }}</span>
              </button>
            </div>
          </div>
          <div v-else class="cookies-modal">
            <div class="checkbox-container">
              <label class="chekbox-label">
                <div class="chekbox-label-wrapper">
                  <input type="checkbox" v-model="cookieSettings.advertising" />
                  <div class="checkbox"></div>
                  <div class="title small">{{ $t("Advertising") }}</div>
                  <div class="desc">
                    {{
                      $t(
                        "Advertising cookies are used to make advertising messages more relevant to you.",
                      )
                    }}
                  </div>
                </div>
              </label>
            </div>

            <div class="checkbox-container">
              <label class="chekbox-label">
                <div class="chekbox-label-wrapper">
                  <input
                    type="checkbox"
                    v-model="cookieSettings.customization"
                  />
                  <div class="checkbox"></div>
                  <div class="title small">
                    {{ $t("Customization Cookies") }}
                  </div>
                  <div class="desc">
                    {{
                      $t(
                        "We use these cookies to gather information on how our website is being used so we can continuously improve it.",
                      )
                    }}
                  </div>
                </div>
              </label>
            </div>

            <div class="checkbox-container">
              <label class="chekbox-label">
                <div class="chekbox-label-wrapper">
                  <input
                    type="checkbox"
                    v-model="cookieSettings.functionality"
                  />
                  <div class="checkbox"></div>
                  <div class="title small">
                    {{ $t("Functionality Cookies") }}
                  </div>
                  <div class="desc">
                    {{
                      $t(
                        "These cookies improve the performance and functionality of our website but are not essential for its use.",
                      )
                    }}
                  </div>
                </div>
              </label>
            </div>

            <div class="buttons">
              <button class="button" @click="acceptAllCookies">
                <span> {{ $t("Accept all") }}</span>
              </button>
              <button @click="saveCookieSettings" class="button">
                <span>{{ $t("Accept selected") }}</span>
              </button>
              <button @click="declineCookies" class="button">
                <span> {{ $t("Decline") }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import Header from "./components/Header.vue";
import Menu from "./components/Menu.vue";
import Footer from "./components/Footer.vue";
import { loadScriptsFromString } from "@/utils/load_scripts";
import Login from "./components/Login.vue";
import Registration from "./components/Registration.vue";
import Cart from "./components/Cart.vue";
import TopUpModal from "./components/TopUp.vue";
import ProductModal from "./components/ProductModal.vue";
import OrderModal from "./components/OrderModal.vue";
import { mapActions, mapState } from "vuex";
import SellSkinModal from "@/components/SellSkinModal.vue";
import PayoutModal from "@/components/PayoutModal.vue";

export default {
  name: "App",
  components: {
    PayoutModal,
    SellSkinModal,
    Header,
    Menu,
    Footer,
    Registration,
    Login,
    Cart,
    TopUpModal,
    ProductModal,
    OrderModal,
  },
  data: function () {
    return {
      appDomain: "",
      regIsAvailable: false,
      pageLoaded: false,
      menuVisible: false,
      nav: [
        {
          title: "HOME",
          link: "/home",
        },
        {
          title: "C2 MARKET",
          link: "/product-list/all",
        },
      ],
      isAuth: false,
      error: "",
      successMessage: "",
      orderHistory: [],
      orderToView: [],
      addToCartChosenItem: "",
      recommendedProducts: [],
      saleProducts: [],
      cartContents: [],
      totalSum: "0.00",
      textPageTitle: "",
      textPageHeader: "",
      textPageContent: "",
      productPageItem: "",
      similarProducts: "",
      imgDomain: "",
      textPageList: "",
      productCategory: "",
      hostname: "",
      footerRequisites: "",
      footerSupportEmail: "",
      footerCopyright: "",
      categoryOptions: [],
      qualityOptions: [],
      currency: "",
      currencyCode: "",
      currencyOptions: "",
      signInModalIsVisible: false,
      signUpModalIsVisible: false,
      cartIsVisible: false,
      topupModalIsVisible: false,
      sellSkinModalIsVisible: false,
      withdrawModalIsVisible: false,
      depositHistory: [],
      searchedProductText: "",
      productsFilteredBySearch: "",
      cookesModalIsVisible: false,

      cookieSettings: {
        essential: true,
        advertising: false,
        customization: false,
        functionality: false,
      },
      settingsManagerIsVisible: false,
      socials: "",
      productModalIsVisible: false,
      appBalance: "",
      newArrivals: [],
      orderModalIsVisible: false,
      availablePaymentMethods: [],
      orderData: {},
      rate: "",
      currencyValue: "",
      rateClean: "",
    };
  },
  computed: {
    ...mapState({
      currentLanguage: (state) => state.app.currentLanguage,
    }),
  },
  watch: {
    currencyCode() {
      this.getGoogleTagManagerSnippet();
      this.getNewArrivals();
      if (this.productPageItem) {
        this.getProductPageContent(this.productPageItem.id);
      }
      if (this.isAuth) {
        this.orderHistory = "";
        this.cartContents = "";
        this.totalSum = "";
        this.getOrderHistory();
        this.getDepositHistory();
        this.getBalance();
        this.getCartContents();
      }
      if (!this.productCategory && this.$route.params.id) {
        this.productCategory = this.$route.params.id;
      }
    },
    $route() {
      window.scrollTo(0, 0);
      this.closeMenu();
      if (this.$route.name === "Home") this.getNewArrivals();
      if (this.isAuth) {
        if (this.$route.name == "Profile") {
          this.getOrderHistory();
          this.getDepositHistory();
        }
      }
    },
  },
  mounted: async function () {
    this.cookiesModal();
    this.hostname = this.getDomainName(window.location.hostname);
    await this.fetchLanguages();
    await this.getCurrencies();
    this.currency = process.env.VUE_APP_CURRENCY;
    this.imgDomain = process.env.VUE_APP_STATIC_DOMAIN;
    this.appDomain = process.env.VUE_APP_DOMAIN;
    this.pageLoaded = true;
    let self = this;
    window.addEventListener("resize", self.menuHeight);
    self.menuHeight();

    self.getGoogleTagManagerSnippet();

    self.$http
      .get(process.env.VUE_APP_API + "is-auth")
      .then((res) => {
        console.log("auth: " + res.data.authenticated);
        self.isAuth = res.data.authenticated;

        if (res.data.authenticated) {
          if (this.$route.name == "Profile") {
            this.getOrderHistory();
            this.getDepositHistory();
          }
          self.getCartContents();
        }
      })
      .catch((res) => {
        if (res.response.status === 401) {
          this.openSignIn();
        }
      });
    this.getCategoryOptions();
    this.getTextPages();
    this.getSocials();
  },
  methods: {
    openCookieSettings() {
      this.cookesModalIsVisible = true;
      this.settingsManagerIsVisible = true;
    },
    orderSubmit(data, type) {
      data.paymentType = type;
      data.currency = this.currency;
      data.saveData = true;
      this.$http
        .post(process.env.VUE_APP_API + "deposit/checkout", data)
        .then((res) => {
          if (res.data.status == "ERROR") {
            this.error = res.data.message;
          } else {
            window.location = res.data.redirect_url;
          }
        })
        .catch((res) => {
          this.error = res.response.data.message;
        });
    },
    getNewArrivals() {
      if (this.$route.name !== "Home") return;
      this.$http
        .get(process.env.VUE_APP_API + "items/list")
        .then((res) => {
          this.newArrivals = res.data.payload;
        })
        .catch((res) => {
          if (res.response.status === 401) {
            this.openSignIn();
          }
        });
    },
    getBalance() {
      if (this.isAuth) {
        this.$http
          .get(
            process.env.VUE_APP_API +
              "user/profile?currency=" +
              this.currencyCode,
          )
          .then((res) => {
            this.appBalance = res.data.balance;
          })
          .catch((res) => {
            if (res.response.status === 401) {
              this.openSignIn();
            }
          });
      }
    },
    setAppBalance(balance) {
      this.appBalance = balance;
    },
    cookiesModal() {
      const cookiesAccepted = localStorage.getItem("cookie_consent");

      if (!cookiesAccepted) {
        this.cookesModalIsVisible = true;
      }
      const storedSettings = localStorage.getItem("cookie_settings");
      if (storedSettings) {
        const parsedSettings = JSON.parse(storedSettings);
        this.cookieSettings.advertising = parsedSettings.advertising || false;
        this.cookieSettings.customization =
          parsedSettings.customization || false;
        this.cookieSettings.functionality =
          parsedSettings.functionality || false;
      }
    },
    acceptAllCookies() {
      localStorage.setItem("cookie_consent", "true");
      this.cookesModalIsVisible = false;
      this.cookieSettings.advertising = true;
      this.cookieSettings.customization = true;
      this.cookieSettings.functionality = true;
      this.saveCookieSettings();
    },
    declineCookies() {
      this.cookesModalIsVisible = false;
      localStorage.setItem("cookie_consent", "false");

      // Disable all optional cookie types
      this.cookieSettings.advertising = false;
      this.cookieSettings.customization = false;
      this.cookieSettings.functionality = false;

      this.saveCookieSettings();
    },
    saveCookieSettings() {
      localStorage.setItem(
        "cookie_settings",
        JSON.stringify(this.cookieSettings),
      );
      this.cookesModalIsVisible = false;
      localStorage.setItem("cookie_consent", "true");
    },
    clearSearch() {
      this.searchedProductText = "";
    },
    searchSubmit(text) {
      this.searchedProductText = text;
      this.productsFilteredBySearch = text;
      if (!this.$route.fullPath.includes("product-list/search")) {
        this.$router.push({ path: "/product-list/search" });
      }
    },
    getCurrencies() {
      this.$http
        .get(process.env.VUE_APP_API + "currencies")
        .then((res) => {
          const newCurrencies = res.data.payload.map(
            ({ code: text, value: id, symbol }) => ({
              text,
              id,
              symbol,
            }),
          );
          this.currencyOptions = newCurrencies;

          if (localStorage.getItem("currency")) {
            this.currencyCode = localStorage.getItem("currency");

            this.currencyValue = this.currencyOptions.find(
              (item) => item.text == this.currencyCode,
            ).id;
          } else {
            this.currencyCode = this.currencyOptions[0].text;

            this.currencyValue = this.currencyOptions[0].id;
          }
          this.checkCurrency();
        })
        .catch(() => {});
    },
    changeCurrency(item) {
      this.currencyCode = item;
      this.checkCurrency();
    },
    checkCurrency() {
      this.currency = this.currencyOptions.find(
        (item) => item.text == this.currencyCode,
      ).symbol;
      this.currencyValue = this.currencyOptions.find(
        (item) => item.text == this.currencyCode,
      ).id;
    },
    setProductCategory(category) {
      let self = this;
      self.productCategory = "1";
      setTimeout(() => {
        self.productCategory = category;
      }, 300);
      if (category) {
        this.$router.push({ path: "/product-list/" + category });
      }
    },
    getCategoryOptions() {
      this.$http
        .get(process.env.VUE_APP_API + "items/filter-data")
        .then((res) => {
          this.categoryOptions = res.data.payload.types;
          this.qualityOptions = res.data.payload.qualities;
        })
        .catch((res) => {
          if (res.response.status === 401) {
            this.openSignIn();
          }
        });
    },
    getDomainName(hostName) {
      return hostName.substring(
        hostName.lastIndexOf(".", hostName.lastIndexOf(".") - 1) + 1,
      );
    },
    closeMenu() {
      this.menuVisible = false;
      document.body.style.overflow = "auto";
    },
    toggleMenu() {
      this.menuVisible = !this.menuVisible;
      window.scrollTo(0, 0);
      if (this.menuVisible) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "auto";
      }
    },
    menuHeight() {
      const doc = document.documentElement;
      doc.style.setProperty("--vh", `${window.innerHeight * 0.01}px`);
    },
    clearError() {
      this.error = "";
    },
    setError(error) {
      this.error = error;
    },
    submitLoginSteam() {
      window.location.replace(
        "https://api.marketofskins.com/auth/steam/redirect",
      );
    },
    login(data) {
      this.$http
        .post(process.env.VUE_APP_API + "login", data)
        .then((res) => {
          if (res.data.status == "OK") {
            this.isAuth = true;
            this.getOrderHistory();
            this.getDepositHistory();
            this.getCartContents();
            this.closeSignIn();
            this.$router.push({ path: "/profile/settings" });
          } else {
            this.error = res.data.message;
          }
        })
        .catch((res) => {
          this.error = res.response.data.message;
        });
    },
    forgotPass(data) {
      let self = this;
      this.$http
        .post(process.env.VUE_APP_API + "user/reset-password", data)
        .then((res) => {
          if (res.data.status == "OK") {
            this.successMessage = "Success";
            this.error = "";
            setTimeout(() => {
              self.successMessage = "";
            }, 2000);
          } else {
            this.error = res.response.data.message;
          }
        })
        .catch((error) => {
          this.error = error.response.data.message;
        });
    },
    registration(data) {
      let self = this;
      if (data.password !== data.passConfirm) {
        this.error = "Password confirmation failed!";
      } else {
        this.$http
          .post(process.env.VUE_APP_API + "register", data)
          .then((res) => {
            if (res.data.status == "OK") {
              this.clearError();
              self.isAuth = true;
              this.closeSignUp();
              self.$router.push({ path: "/profile/settings" });
            } else {
              this.error = res.data.message;
            }
          })
          .catch((res) => {
            if (res.response && res.response.data && res.response.data.errors) {
              const firstErrorField = Object.keys(res.response.data.errors)[0];
              this.error = res.response.data.errors[firstErrorField][0];
            } else {
              this.error = res.response.data.message || "Registration failed";
            }
          });
      }
    },
    logout() {
      this.$http
        .post(process.env.VUE_APP_API + "logout")
        .then(() => {
          if (this.$route.name != "Home") {
            this.$router.push({ path: "/" });
          }
          this.isAuth = false;
          this.$router.push({ path: "/" });
        })
        .catch((res) => {
          if (res.response.status === 401) {
            console.log(res);
          }
        });
    },
    getDepositHistory() {
      this.$http
        .get(process.env.VUE_APP_API + "deposit/history")
        .then((res) => {
          this.depositHistory = res.data.payload;
        })
        .catch((res) => {
          if (res.response.status === 401) {
            this.openSignIn();
          }
        });
    },
    getOrderHistory() {
      this.$http
        .get(process.env.VUE_APP_API + "purchase/history")
        .then((res) => {
          this.orderHistory = res.data.payload;
        })
        .catch((res) => {
          if (res.response.status === 401) {
            this.openSignIn();
          }
        });
    },
    formatDate(num) {
      let objectDate = new Date(num * 1000);
      let day = objectDate.getDate();
      if (day < 10) {
        day = "0" + day;
      }
      let month = objectDate.getMonth() + 1;
      if (month < 10) {
        month = `0${month}`;
      }
      let year = objectDate.getFullYear();

      function padTo2Digits(x) {
        return String(x).padStart(2, "0");
      }
      let hoursAndMinutes =
        padTo2Digits(objectDate.getHours()) +
        ":" +
        padTo2Digits(objectDate.getMinutes());
      let format = day + "." + month + "." + year + " " + hoursAndMinutes;

      return format;
    },
    removeOneFromCart(item, id) {
      let self = this;

      let cartItem = {
        id: id,
        count: "1",
      };
      if (item.count > 1) {
        cartItem = {
          id: id,
          count: item.count - 1,
        };
      }
      this.$http
        .post(process.env.VUE_APP_API + "cart/add", cartItem)
        .then((res) => {
          self.cartContents = res.data.cart;
          this.getCartContents();
        })
        .catch((res) => {
          if (res.response.status === 401) {
            this.openSignIn();
          }
        });
    },
    removeFromCart(item, id) {
      let self = this;

      let cartItem = {
        id: id,
      };

      this.$http
        .post(process.env.VUE_APP_API + "cart/remove", cartItem)
        .then((res) => {
          self.cartContents = res.data.cart;
          this.getCartContents();
        })
        .catch((res) => {
          if (res.response.status === 401) {
            this.openSignIn();
          }
        });
    },
    addToCart(item, id) {
      if (!this.isAuth) {
        this.openSignIn();
      } else {
        let self = this;

        let cartItem = {
          id: id,
          count: 1,
        };

        for (var i = 0; i < this.cartContents.length; i++) {
          if (this.cartContents[i].item_id == id) {
            let cartItemCount = this.cartContents[i].count;
            cartItem = {
              id: id,
              count: cartItemCount + 1,
            };
            break;
          }
        }

        this.$http
          .post(process.env.VUE_APP_API + "cart/add", cartItem)
          .then((res) => {
            this.error = "";
            this.getCartContents();
            self.addToCartChosenItem = cartItem.id;
            self.cartContents = res.data.cart;
            setTimeout(() => {
              self.addToCartChosenItem = "";
            }, 2000);
          })
          .catch((res) => {
            if (res.response.status === 401) {
              this.openSignIn();
            } else {
              this.error = res.response.data.message;
            }
          });
      }
    },
    getCartContents() {
      this.$http
        .get(process.env.VUE_APP_API + "cart/contents")
        .then((res) => {
          this.cartContents = res.data.cart;

          let total = 0;
          for (let i in this.cartContents) {
            let newPrice;
            if (this.cartContents[i].count > 1) {
              newPrice =
                this.cartContents[i].count *
                parseFloat(this.cartContents[i].item.price);
            } else {
              newPrice = parseFloat(this.cartContents[i].item.price);
            }
            total += newPrice;
          }
          this.totalSum = total.toFixed(2);
        })
        .catch((res) => {
          if (res.response.status === 401) {
            this.openSignIn();
          }
        });
    },
    goToProduct(item) {
      this.$router.push({
        path: "/product/" + item.id,
        params: { data: item.id },
      });
      this.getProductPageContent(item.id);
    },
    getProductPageContent(id) {
      this.productPageItem = "";
      this.similarProducts = "";
      this.$http
        .get(process.env.VUE_APP_API + "items/" + id)
        .then((res) => {
          this.productPageItem = res.data.payload;
          this.similarProducts = res.data.payload.similar;
        })
        .catch((res) => {
          if (res.response.status === 401) {
            this.openSignIn();
          }
        });
    },
    goToPage(item) {
      let newItem;
      if (item == "privacy") {
        for (let i = 0; i < this.textPageList.length; i++) {
          if (this.textPageList[i].title.toLowerCase().includes("privacy")) {
            newItem = this.textPageList[i].id;
          }
        }
      } else if (item == "terms") {
        for (let i = 0; i < this.textPageList.length; i++) {
          if (this.textPageList[i].title.toLowerCase().includes("terms")) {
            newItem = this.textPageList[i].id;
          }
        }
      } else if (item == "cookie") {
        for (let i = 0; i < this.textPageList.length; i++) {
          if (this.textPageList[i].title.toLowerCase().includes("cookie")) {
            newItem = this.textPageList[i].id;
          }
        }
      } else {
        newItem = item;
      }
      this.$router.push({
        path: "/pages/" + newItem,
        params: { data: newItem },
      });
      this.getTextPageContent(newItem);
      this.closeSignUp();
    },
    getTextPageContent(id) {
      this.textPageTitle = "";
      this.textPageHeader = "";
      this.textPageContent = "";
      this.$http
        .get(process.env.VUE_APP_API + "static-pages/" + id)
        .then((res) => {
          this.textPageTitle = res.data.payload.title;
          this.textPageContent = res.data.payload.content;
          this.textPageHeader = res.data.payload.header;
        })
        .catch((res) => {
          if (res.response.status === 401) {
            this.openSignIn();
          }
        });
    },
    getTextPages() {
      this.$http
        .get(process.env.VUE_APP_API + "static-pages")
        .then((res) => {
          this.textPageList = res.data.payload;
        })
        .catch((res) => {
          if (res.response.status === 401) {
            this.openSignIn();
          }
        });
    },
    openTopup() {
      this.topupModalIsVisible = true;
      document.body.style.overflow = "hidden";
    },
    closeTopup() {
      this.topupModalIsVisible = false;
      document.body.style.overflow = "auto";
      this.error = "";
    },
    openSellSkinModal() {
      this.sellSkinModalIsVisible = true;
      document.body.style.overflow = "hidden";
    },
    closeSellSkinModal() {
      this.sellSkinModalIsVisible = false;
      document.body.style.overflow = "auto";
      this.error = "";
    },
    openCart() {
      this.cartIsVisible = true;
      document.body.style.overflow = "hidden";
    },
    openPayoutModal() {
      this.withdrawModalIsVisible = true;
      document.body.style.overflow = "hidden";
    },
    closePayoutModal() {
      this.withdrawModalIsVisible = false;
      document.body.style.overflow = "auto";
      this.error = "";
    },
    closeCart() {
      this.cartIsVisible = false;
      document.body.style.overflow = "auto";
      this.error = "";
    },
    openSignIn() {
      this.signUpModalIsVisible = false;
      this.signInModalIsVisible = true;
      document.body.style.overflow = "hidden";
    },
    closeSignIn() {
      this.signInModalIsVisible = false;
      document.body.style.overflow = "auto";
      this.error = "";
    },
    openSignUp() {
      this.signInModalIsVisible = false;
      this.signUpModalIsVisible = true;
      document.body.style.overflow = "hidden";
    },
    closeSignUp() {
      this.signUpModalIsVisible = false;
      document.body.style.overflow = "auto";
      this.error = "";
    },
    openRecover() {
      this.$router.push({ path: "/recover" });
    },
    openOrderModal(data) {
      this.topupModalIsVisible = false;
      this.orderModalIsVisible = true;
      document.body.style.overflow = "hidden";
      this.orderData = data;
    },
    closeOrderModal() {
      this.orderModalIsVisible = false;
      document.body.style.overflow = "auto";
      this.error = "";
    },
    getGoogleTagManagerSnippet() {
      this.$http
        .get(process.env.VUE_APP_API + "settings")
        .then((res) => {
          this.footerRequisites = res.data.payload.requisites;
          this.footerSupportEmail = res.data.payload.support_email;
          this.footerCopyright = res.data.payload.copyright;
          this.regIsAvailable = res.data.payload.is_reg_enabled;
          this.availablePaymentMethods = res.data.payload.payment_methods;

          if (this.currencyCode) {
            console.log(this.currencyValue);
            const currencyRate = this.currencyValue || 1;
            const internalCurrencyEurRate =
              res.data.payload.internal_currency.exchange_rate_eur || 1;
            this.rate = (currencyRate * internalCurrencyEurRate).toFixed(2);
            this.rateClean = currencyRate * internalCurrencyEurRate;
          }

          console.log(this.availablePaymentMethods);

          const snippet = res.data.payload.google_tag;
          if (snippet) {
            loadScriptsFromString(snippet);
          }
        })
        .catch((error) => {
          console.error("Error fetching Google Tag Manager snippet:", error);
        });
    },
    getSocials() {
      this.$http
        .get(process.env.VUE_APP_API + "social-links")
        .then((res) => {
          this.socials = res.data.payload;
        })
        .catch((error) => {
          console.error(error);
        });
    },
    ...mapActions("app", ["fetchLanguages"]),
  },
};
</script>
